import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { AvatarService } from '../../services/avatar.service';
import { AuthService } from '../../services/auth.service';
import { UsersService, DEFAULT_TEMP_PASSWORD } from '../../services/users.service';
import { PermissionService } from '../../services/permission.service';
import { ConfirmationService } from '../../shared/services/confirmation.service';
import { ERPPermissions } from '../../constants/permissions.constants';

@Component({
  selector: 'app-user-view-modal',
  templateUrl: './user-view-modal.component.html',
  styleUrls: ['./user-view-modal.component.css']
})
export class UserViewModalComponent implements OnChanges, OnDestroy {
  @Input() show: boolean = false;
  @Input() user: any | null = null;
  @Output() closeModal = new EventEmitter<void>();

  avatarDataUrl: string | null = null;
  avatarLoading = false;

  showTempPasswordPanel = false;
  useDefaultTempPassword = true;
  tempPassword = DEFAULT_TEMP_PASSWORD;
  showTempPassword = false;
  settingTempPassword = false;
  tempPasswordMessage = '';
  tempPasswordError = '';

  readonly canEditUsers: boolean;
  readonly defaultTempPassword = DEFAULT_TEMP_PASSWORD;

  private avatarSub?: Subscription;
  private lastUserId?: number;

  constructor(
    private avatarService: AvatarService,
    private usersService: UsersService,
    private authService: AuthService,
    private permissionService: PermissionService,
    private confirmationService: ConfirmationService
  ) {
    this.canEditUsers = this.permissionService.hasPermission(ERPPermissions.USERS_EDIT);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const userChanged = !!changes['user'];
    const showChanged = !!changes['show'];

    if (userChanged || showChanged) {
      this.resetTempPasswordState();
    }

    if (!this.show || !this.user?.id) {
      if (userChanged || showChanged) {
        this.resetAvatarState();
      }
      return;
    }

    const userId = Number(this.user.id);
    if (!Number.isFinite(userId)) {
      this.resetAvatarState();
      return;
    }

    if (userChanged || showChanged) {
      if (this.lastUserId !== userId) {
        this.lastUserId = userId;
        this.loadAvatar(userId);
      }
    }
  }

  ngOnDestroy(): void {
    this.avatarSub?.unsubscribe();
  }

  close(): void {
    this.closeModal.emit();
  }

  onTempPasswordPanelChange(enabled: boolean): void {
    this.showTempPasswordPanel = enabled;

    if (!enabled) {
      this.tempPasswordMessage = '';
      this.tempPasswordError = '';
      this.showTempPassword = false;
    }
  }

  onDefaultTempPasswordChange(useDefault: boolean): void {
    this.useDefaultTempPassword = useDefault;

    if (useDefault) {
      this.tempPassword = DEFAULT_TEMP_PASSWORD;
      this.showTempPassword = false;
    } else {
      this.tempPassword = '';
    }

    this.tempPasswordMessage = '';
    this.tempPasswordError = '';
  }

  toggleTempPasswordVisibility(): void {
    this.showTempPassword = !this.showTempPassword;
  }

  setTemporaryPassword(): void {
    if (!this.showTempPasswordPanel || !this.user?.email || this.settingTempPassword) {
      return;
    }

    const validation = this.usersService.validatePassword(this.tempPassword);
    if (!validation.isValid) {
      this.tempPasswordError = validation.errors.join('. ');
      this.tempPasswordMessage = '';
      return;
    }

    const userName = this.user.name || this.user.email;
    this.confirmationService.confirm({
      title: 'Definir senha temporária',
      message: `Definir senha temporária para "${userName}"? O usuário precisará alterá-la no próximo login.`,
      confirmText: 'Confirmar',
      cancelText: 'Cancelar',
      confirmButtonClass: 'btn-primary',
      cancelButtonClass: 'btn-secondary',
      type: 'question'
    }).subscribe(confirmed => {
      if (!confirmed) {
        return;
      }

      this.applyTemporaryPassword();
    });
  }

  private applyTemporaryPassword(): void {
    const updatedBy = this.getCurrentUserEmail();
    if (!updatedBy) {
      this.tempPasswordError = 'Não foi possível identificar o usuário atual. Faça login novamente.';
      return;
    }

    this.settingTempPassword = true;
    this.tempPasswordMessage = '';
    this.tempPasswordError = '';

    this.usersService.setTemporaryPassword(this.user!.email, this.tempPassword, updatedBy).subscribe({
      next: (response) => {
        this.settingTempPassword = false;
        if (response?.success !== false) {
          this.tempPasswordMessage = 'Senha temporária definida com sucesso.';
        } else {
          this.tempPasswordError = response?.message || 'Erro ao definir senha temporária.';
        }
      },
      error: () => {
        this.settingTempPassword = false;
      }
    });
  }

  private getCurrentUserEmail(): string {
    const lastLoggedEmail = localStorage.getItem('lastLoggedEmail');
    if (lastLoggedEmail) {
      return lastLoggedEmail;
    }

    const currentUser = this.authService.currentUserValue;
    if (currentUser?.email) {
      return currentUser.email;
    }

    return this.authService.getLastLoggedEmail() || '';
  }

  private resetTempPasswordState(): void {
    this.showTempPasswordPanel = false;
    this.useDefaultTempPassword = true;
    this.tempPassword = DEFAULT_TEMP_PASSWORD;
    this.showTempPassword = false;
    this.settingTempPassword = false;
    this.tempPasswordMessage = '';
    this.tempPasswordError = '';
  }

  private loadAvatar(userId: number): void {
    this.avatarSub?.unsubscribe();
    this.avatarLoading = true;
    this.avatarDataUrl = null;

    this.avatarSub = this.avatarService.getAvatarOptional(userId).subscribe({
      next: (res) => {
        const base64 = res?.avatar || '';
        this.avatarDataUrl = base64 ? this.toImageDataUrl(base64) : null;
        this.avatarLoading = false;
      },
      error: () => {
        this.avatarLoading = false;
        this.avatarDataUrl = null;
      }
    });
  }

  private resetAvatarState(): void {
    this.avatarSub?.unsubscribe();
    this.avatarSub = undefined;
    this.avatarLoading = false;
    this.avatarDataUrl = null;
    this.lastUserId = undefined;
  }

  private toImageDataUrl(base64OrDataUrl: string): string {
    const value = (base64OrDataUrl || '').trim();
    if (!value) {
      return '';
    }
    if (value.startsWith('data:')) {
      return value;
    }

    const mime = this.detectImageMimeType(value);
    return `data:${mime};base64,${value}`;
  }

  private detectImageMimeType(base64: string): string {
    if (base64.startsWith('/9j/')) {
      return 'image/jpeg';
    }
    if (base64.startsWith('iVBORw0KGgo')) {
      return 'image/png';
    }
    if (base64.startsWith('R0lGOD')) {
      return 'image/gif';
    }
    return 'image/png';
  }

  getStatusBadgeClass(active: boolean | undefined): string {
    if (active === undefined || active === null) {
      return 'badge bg-secondary';
    }
    return active ? 'badge bg-success' : 'badge bg-danger';
  }

  getClientBadgeClass(isClient: boolean | undefined): string {
    if (isClient === undefined || isClient === null) {
      return 'badge bg-secondary';
    }
    return isClient ? 'badge bg-primary' : 'badge bg-info';
  }
}
